import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/observable/throw';
import { CookieService } from 'ngx-cookie-service';
import { DialogService } from 'ng2-bootstrap-modal';
import { SessionExpiredPopupComponent } from '../session-expired-popup/session-expired-popup.component';
import { isSsoAllowedUrl } from './ssoRestrict.guard';

// Per-tab marker for an SSO session. Lives in sessionStorage so it survives
// the cookie/localStorage wipe on logout or expiry (and dies with the tab),
// letting SsoLoginBlockGuard keep SSO users off the login page.
const SSO_SESSION_KEY = 'sso_session';

// Catches backend 401 {"detail": "Invalid token."} responses for SSO users on
// SSO-authorised routes (see HttpClientService), wipes the session and shows
// a single "session expired" popup whose button closes the tab.
@Injectable({ providedIn: 'root' })
export class SessionExpiredService {
    private popupOpen = false;

    constructor(private dialogService: DialogService, private cookie: CookieService, private router: Router) {
        // Created at app boot (HttpClientService depends on it), so an SSO
        // session that predates the marker still gets one before anything
        // can wipe cookies/localStorage.
        this.isSsoSession();
    }

    markSsoSession() {
        sessionStorage.setItem(SSO_SESSION_KEY, 'true');
    }

    isSsoSession(): boolean {
        const flagged = this.cookie.get('sso_user') === 'true' || localStorage.getItem('sso_user') === 'true';
        if (flagged) {
            this.markSsoSession();
        }
        return flagged || sessionStorage.getItem(SSO_SESSION_KEY) === 'true';
    }

    // Clears cookies/localStorage but keeps the per-tab SSO marker.
    endSsoSession() {
        this.markSsoSession();
        this.cookie.delete('token', '/');
        this.cookie.delete('sso_user', '/');
        this.cookie.deleteAll();
        localStorage.clear();
    }

    showClosePopup(title: string = 'Session Expired', message: string = 'Your session has expired.') {
        if (this.popupOpen) {
            return;
        }
        this.popupOpen = true;
        this.dialogService.addDialog(SessionExpiredPopupComponent, { title: title, message: message },
            { closeByClickingOutside: false });
    }

    // SSO logout: never routes to login. Build logout$ before calling this -
    // auth headers are captured at build time, so the session can be wiped
    // straight away. Success closes the tab; otherwise show the popup.
    logoutSso(logout$: Observable<any>, done: () => void) {
        this.endSsoSession();
        const showLoggedOut = () => this.showClosePopup('Logged Out', 'You have been logged out.');
        logout$.subscribe(() => {
            done();
            window.close();
            setTimeout(() => {
                if (!window.closed) {
                    showLoggedOut();
                }
            }, 300);
        }, () => {
            done();
            showLoggedOut();
        });
    }

    handle(error: any): Observable<any> {
        if (this.isSsoSession() && isSsoAllowedUrl(this.router.url) && this.isInvalidToken(error)) {
            this.endSsoSession();
            this.showClosePopup();
        }
        // Re-throw so existing component error handlers still run.
        return Observable.throw(error);
    }

    private isInvalidToken(error: any): boolean {
        if (!error || error.status !== 401) {
            return false;
        }
        let body: any;
        try {
            body = typeof error.json === 'function' ? error.json() : error.error;
        } catch (e) {
            body = typeof error.text === 'function' ? error.text() : '';
        }
        const text = typeof body === 'string' ? body : JSON.stringify(body || '');
        return /invalid token/i.test(text);
    }
}
